/**
 * ClosurePanel (13-26): the end of a record's life, over 13-21's builders (FIELDSET §1.8, §3.5; D-13-L-3, -14, -17).
 *
 * - **Which steps:** the moves `closureVerbsFor(row)` allows from the record's `Status`, each shown only when 13-22's
 *   gate allows it for this person (`canDoRecordVerb`, with the preparer from the trail, D-13-L-19). A step a role
 *   can't use is hidden, never shown disabled: a Care Manager never sees *Approve and close*. An Oversight member
 *   who readied the incident sees why someone else must approve it.
 *   - Complaints / Feedback: *Close* (one step, a Care Manager). Incidents: *Ready for approval* (a Care Manager),
 *     then *Approve and close* (Oversight, never the readier). CI: *Approve*, then *Close* (Oversight). Risk:
 *     *Risk review* and *Retire* (Oversight). Every register: *Cancel* (a reason) and, once Completed, *Reopen*
 *     (Oversight, a reason).
 * - **Inputs:** each step's own columns (`closureInputColumns`: never `Status`, `ClosureStatus`, `ClosedOn` or a
 *   stamp, and on CI never the CI engine's `ReviewEvidence` / `ReviewVerdict`, D-13-17-7), the close day, the next
 *   review day and the review text, and a required reason where §1.8 / D-13-L-17 ask for one.
 * - **Refusals** come from the builder and sit at their field; a closure column recorded by another panel (the
 *   findings, open disclosure) is named with where to record it.
 * - **The confirm** lists every change the builder makes and names any open Closure Link: "2 links are still open:
 *   CI-12, CPR-3. Close anyway?" (D-13-L-14: never a block). Reasons go to the `StateChange` Notes.
 * - **Return to the Care Manager** (ISS-259, D-13-33): Oversight sends an Evaluating incident back to the Care
 *   Manager (`Evaluating` → `Actioning`) with a reason. It replaces 13-26's disabled placeholder (D-13-26-3).
 * - **The verification checks** (13.2-08, D-13.2-P-8): on an Actioning or Evaluating incident the panel reads its
 *   follow-ups (`useFollowUps`, the page's one shared AR read), works out 13.2-06's checklist and shows it above the
 *   steps, so the Care Manager sees what Oversight will see. *Approve and close* is given the open checks as
 *   `openChecks`; while the follow-ups are loading or failed it is given none, so the builder refuses
 *   `checks-unknown` (fail closed, by design), and the panel says so in the builder's own words. *Ready for approval*
 *   is never gated on the checks.
 * - **Drafting help** (13.2-08, D-13.2-A-2, D-13.2-P-9): on *Ready for approval*, "Copy for Nina / Hana" puts a
 *   closure-drafting brief (`closureBrief`) on the clipboard, with links to Nina or Hana (by business unit) and to the
 *   Q&S Advisor. A link shows only when its address is confirmed (`DRAFTING_TOOLS`). No model call, no network write.
 * - **Close as a duplicate** (13.2-10, D-13.2-P-10; Oversight only, 13-22): its own step. It asks for the incident
 *   kept, prefilled with the record page's top possible match (`DuplicatesContext`), reads that record
 *   (`useKeptRecord`, the row only) and gives it to the builder as `ctx.kept`, so 13.2-05's refusals (itself, not
 *   found, cancelled, already a duplicate, another register) come back in words at the input. The note is optional.
 *   On save the duplicate's own rows go with the save, then the kept record's Note is posted on ITS trail
 *   (`writer.postRecordSiblings`); a Note that doesn't post is owed (persisted, retried), never dropped.
 * Every write goes through `VerbForm` → the runtime's Writer; nothing here writes a closure column itself.
 */

import { useContext, useMemo, useState } from 'react';
import type { ContextType } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../ui';
import VerbForm from '../VerbForm';
import type { VerbAfterCommit, VerbBuilder, VerbExtraField } from '../VerbForm';
import { useActionRuntime } from '../../../hooks/useActionRuntime';
import { DuplicatesContext, useKeptRecord } from '../../../hooks/useDuplicates';
import type { KeptRecord } from '../../../hooks/useDuplicates';
import { matchWords } from '../../../lib/duplicates';
import IncidentChecks from '../IncidentChecks';
import checkStyles from '../IncidentChecks.module.css';
import { useFollowUps } from '../../../hooks/useFollowUps';
import { DRAFTING_TOOLS, closureBrief, draftingToolsFor } from '../../../lib/closureBrief';
import type { DraftingTool } from '../../../lib/closureBrief';
import type { FollowUp } from '../../../lib/followUps';
import { incidentChecklist, openChecks } from '../../../lib/incidentVerify';
import type { CheckItem } from '../../../lib/incidentVerify';
import { fieldOf, verbColumns } from '../../../config/fieldset/registerFields';
import { liveChoice } from '../../../config/fieldset/live';
import { REGISTERS } from '../../../config/registers';
import { isOversight } from '../../../lib/actionAuthority';
import { todayMelbourne } from '../../../lib/fieldValues';
import { RECORD_AUTHORITY_TEXT, canDoRecordVerb } from '../../../lib/recordAuthority';
import {
  CHECKS_UNKNOWN_MESSAGE,
  CLOSURE_INPUT_COLUMN,
  CLOSURE_LABELS,
  REASON_MIN,
  REASON_REQUIRED,
  buildClosureVerb,
  checksOpenMessage,
  closesRecord,
  closureInputColumns,
  closureTakes,
  closureVerbsFor,
} from '../../../lib/recordClosure';
import type { ClosureInput, ClosureVerb, DuplicateBuild } from '../../../lib/recordClosure';
import { ENGINE_VERBS, VERB_LABELS } from '../../../lib/recordVerbs';
import type { RecordVerbBuild } from '../../../lib/recordVerbs';
import { preparedByUpn } from '../../../services/recordService';
import type { RecordIO } from '../../../services/recordService';
import type { RecordRegister, RecordRow } from '../../../types/records';
import type { PanelProps } from './registry';
import styles from '../record.module.css';

const INVESTIGATION_REQUIRED = 'InvestigationRequired';
const [INVESTIGATION_YES] = liveChoice('incidents', INVESTIGATION_REQUIRED, 'Yes');
/** 13.2-08: the incident states the checklist shows on (being readied, or waiting for Oversight). */
const CHECKED_STATES: readonly string[] = liveChoice('incidents', 'Status', 'Actioning', 'Evaluating');

/** 13.2-08: an incident's verification checks as the panel knows them. `ready.open` is approve's `openChecks`. */
type Checks =
  | { status: 'loading' }
  | { status: 'error'; error: string | null }
  | { status: 'ready'; items: CheckItem[]; open: string[] };

/** The step's button words. Cancel names its object, so it never reads as "don't do this". */
const BUTTON_WORDS: Readonly<Record<ClosureVerb, string>> = {
  ...CLOSURE_LABELS,
  cancel: 'Cancel the record',
  reopen: 'Reopen',
};

/** The confirm's question when open links remain ("Close anyway?"). */
const ANYWAY_WORD: Readonly<Partial<Record<ClosureVerb, string>>> = { close: 'Close', approveAndClose: 'Close', cancel: 'Cancel', riskRetire: 'Retire' };

/** Return, cancel, close as a duplicate, reopen and retire are charcoal (they end or undo a step); the rest are teal. */
const SOLEMN: ReadonlySet<ClosureVerb> = new Set(['returnForRework', 'cancel', 'closeAsDuplicate', 'reopen', 'riskRetire']);

/** What each step does, in a sentence above its inputs. */
function introOf(verb: ClosureVerb, register: RecordRegister): string {
  switch (verb) {
    case 'readyForApproval':
      return 'Record the closure assessment. The incident then waits for Oversight: someone other than you approves and closes it.';
    case 'approveAndClose':
      return 'Approving closes the incident. You and today are recorded as the approval.';
    case 'returnForRework':
      return 'The incident goes back to the Care Manager (Actioning) to correct the assessment, then comes back for approval. Say what needs changing: it goes on the trail.';
    case 'close':
      return register === 'ci' ? 'Closing completes the improvement. It must be approved first.' : 'Closing completes the record.';
    case 'ciApprove':
      return "Records you as approving the improvement's resolution. Close it afterwards.";
    case 'riskReview':
      return 'Set the next review day and record what the review found. Acceptance is stamped with your name only when it becomes Yes.';
    case 'riskRetire':
      return 'Retiring completes the risk. It must have been reviewed first.';
    case 'cancel':
      return 'Cancelling closes the record without resolving it, so no close date is recorded.';
    case 'closeAsDuplicate':
      return 'Closing as a duplicate cancels this incident in favour of the one you keep, and notes it on both trails.';
    case 'reopen':
      return 'Reopening sets the record back to Actioning and clears its close date. The earlier values stay on the trail.';
  }
}

/** The verb that records a column, when it is an earlier panel's (the engine verbs). */
function recordedUnder(register: RecordRegister, column: string): string | null {
  const verb = ENGINE_VERBS.find((v) => verbColumns(register, v).includes(column));
  if (!verb) return null;
  // 13.2-09: a report column is added under the "Complete the report" panel, not under the trail's past-tense label.
  if (verb === 'complete') return 'Add it under Complete the report first.';
  return `Record it under ${VERB_LABELS[verb]} first.`;
}

/** The inputs a step takes that are not columns. */
function extraFieldsOf(verb: ClosureVerb, row: RecordRow, today: string): VerbExtraField[] {
  const register = row.register;
  const takes = closureTakes(verb);
  const out: VerbExtraField[] = [];
  if (takes.includes('closedOn')) {
    const column = CLOSURE_INPUT_COLUMN.closedOn as string;
    out.push({
      key: 'closedOn',
      column,
      label: fieldOf(register, column)?.label ?? column,
      kind: 'day',
      initial: today,
      hint: 'The day it was resolved: today, unless it was earlier.',
    });
  }
  if (takes.includes('nextReviewOn')) {
    const column = CLOSURE_INPUT_COLUMN.nextReviewOn as string;
    out.push({
      key: 'nextReviewOn',
      column,
      label: `${fieldOf(register, column)?.label ?? column}: the next review`,
      kind: 'day',
      hint: 'After today.',
    });
  }
  if (takes.includes('reviewNote') && verb === 'approveAndClose') {
    // ISS-260 (D-13-33): the report scrutiny before closure (G-60), required when an investigation was required.
    const required = row.fields[INVESTIGATION_REQUIRED] === INVESTIGATION_YES;
    out.push({
      key: 'reviewNote',
      label: 'Your scrutiny of the investigation report',
      kind: 'textarea',
      hint: required
        ? 'Required: an investigation was required. Say what you checked in the report and its findings. It goes on the trail as the review row.'
        : 'Optional: no investigation was required. If you write it, it goes on the trail as the review row.',
    });
  } else if (takes.includes('reviewNote')) {
    out.push({ key: 'reviewNote', label: 'What the review found', kind: 'textarea', hint: 'It goes on the trail as the review row.' });
  }
  if (takes.includes('communicationNote')) {
    out.push({
      key: 'communicationNote',
      label: 'What the people affected were told, and when (optional)',
      kind: 'textarea',
      hint: 'The reports, findings or outcome given to the people affected: what, to whom (by role, never a name) and when. It goes on the trail as a communication row.',
    });
  }
  return out;
}

export default function ClosurePanel({ row, siblings, actor, openLinks = [], onSaved }: PanelProps) {
  const register = row.register;
  // 13.2-08: an incident's follow-ups → its checklist (the page's one shared AR read). Other registers read nothing.
  const incident = register === 'incidents';
  const followUps = useFollowUps(incident ? row.ref : null);
  const [checkedAt] = useState(() => new Date());
  const checks = useMemo<Checks | null>(() => {
    if (!incident) return null;
    if (followUps.status === 'loading') return { status: 'loading' };
    if (followUps.status === 'error') return { status: 'error', error: followUps.error };
    const items = incidentChecklist(row, followUps.followUps, checkedAt);
    return { status: 'ready', items, open: openChecks(items) };
  }, [incident, followUps.status, followUps.error, followUps.followUps, row, checkedAt]);
  /** Approve's `openChecks`, only when known: loading or failed → none, so the builder refuses `checks-unknown`. */
  const open = checks?.status === 'ready' ? checks.open : undefined;
  const knownFollowUps = followUps.status === 'ready' ? followUps.followUps : null;
  const preparer = useMemo(() => preparedByUpn(siblings), [siblings]);
  const ctx = useMemo(() => ({ preparedByUpn: preparer }), [preparer]);
  const moves = useMemo(() => closureVerbsFor(row), [row]);
  const allowed = useMemo(() => moves.filter((v) => canDoRecordVerb(v, row, actor, ctx).ok), [moves, row, actor, ctx]);
  const [chosen, setChosen] = useState<ClosureVerb | null>(null);
  const [today] = useState(() => todayMelbourne(new Date()));
  const picked = chosen && allowed.includes(chosen) ? chosen : null;

  // An Oversight member refused approve-and-close for who prepared it (D-13-L-19): say why, never show the button.
  const approveRefusal = useMemo(() => {
    if (!moves.includes('approveAndClose') || allowed.includes('approveAndClose') || !isOversight(actor)) return null;
    const r = canDoRecordVerb('approveAndClose', row, actor, ctx);
    return !r.ok && (r.reason === 'self-approval' || r.reason === 'preparer-unknown') ? RECORD_AUTHORITY_TEXT[r.reason] : null;
  }, [moves, allowed, actor, row, ctx]);

  if (allowed.length === 0 && !approveRefusal) {
    return <p className={styles.hint}>No closure step is yours to take on this record while it is {String(row.fields[REGISTERS[register].fields.status] ?? 'blank')}.</p>;
  }

  const showChecks = checks !== null && CHECKED_STATES.includes(String(row.fields.Status ?? ''));

  return (
    <div className={styles.verbForm}>
      {showChecks && checks && (
        <PanelChecks checks={checks} approving={allowed.includes('approveAndClose')} onRetry={followUps.reload} />
      )}
      <div className={styles.verbs} role="group" aria-label="Closure steps">
        {allowed.map((verb) => (
          <Button
            key={verb}
            type="button"
            variant={picked === verb ? 'primary' : 'secondary'}
            className={[picked === verb && SOLEMN.has(verb) ? styles.solemn : '', picked === verb ? styles.verbPicked : ''].filter(Boolean).join(' ') || undefined}
            aria-pressed={picked === verb}
            onClick={() => setChosen(picked === verb ? null : verb)}
          >
            {BUTTON_WORDS[verb]}
          </Button>
        ))}
      </div>

      {approveRefusal && <p className={styles.verbNote}>{approveRefusal}</p>}

      {picked === 'closeAsDuplicate' && <DuplicateStep row={row} siblings={siblings} actor={actor} onSaved={onSaved} />}
      {picked && picked !== 'closeAsDuplicate' && (
        <ClosureStep
          key={picked}
          verb={picked}
          row={row}
          siblings={siblings}
          actor={actor}
          preparer={preparer}
          openLinks={openLinks}
          today={today}
          openChecks={open}
          followUps={knownFollowUps}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

/**
 * 13.2-08: the checklist above the steps, with what *Approve and close* will say, in the builder's own words: unknown
 * checks (`CHECKS_UNKNOWN_MESSAGE`) and open ones (`checksOpenMessage`).
 */
function PanelChecks({ checks, approving, onRetry }: { checks: Checks; approving: boolean; onRetry: () => void }) {
  return (
    <section className={checkStyles.panelChecks} aria-labelledby="closure-checks-h">
      <h4 id="closure-checks-h" className={checkStyles.panelHead}>
        Verification checks
      </h4>
      <p className={styles.hint}>
        {approving
          ? 'Approve and close is refused while any check is open.'
          : 'Oversight checks these before closing. You can ready it with checks still open: they see this same list.'}
      </p>
      {checks.status === 'loading' && (
        <p className={styles.hint} role="status">
          Reading the follow-ups to check this incident…
        </p>
      )}
      {checks.status === 'error' && (
        <div className={styles.notice} role="alert">
          <p className={checkStyles.flat}>{CHECKS_UNKNOWN_MESSAGE}</p>
          <p className={checkStyles.flat}>The follow-ups couldn't be read{checks.error ? `: ${checks.error}` : ''}.</p>
          <Button type="button" variant="secondary" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}
      {checks.status === 'ready' && <IncidentChecks items={checks.items} />}
      {checks.status === 'ready' && approving && checks.open.length > 0 && <p className={styles.notice}>{checksOpenMessage(checks.open)}</p>}
    </section>
  );
}

interface StepProps extends Omit<PanelProps, 'openLinks'> {
  verb: ClosureVerb;
  preparer: string | undefined;
  openLinks: readonly string[];
  today: string;
  /** 13.2-08: approve's open verification checks; `undefined` while unknown (so it is refused `checks-unknown`). */
  openChecks: readonly string[] | undefined;
  /** 13.2-08: the incident's follow-ups for the drafting brief, or null while unknown. */
  followUps: readonly FollowUp[] | null;
}

function ClosureStep({ verb, row, siblings, actor, preparer, openLinks, today, openChecks: checksOpen, followUps, onSaved }: StepProps) {
  const register = row.register;
  const columns = useMemo(() => closureInputColumns(register, verb), [register, verb]);
  const extraFields = useMemo(() => extraFieldsOf(verb, row, today), [verb, row, today]);
  const closing = closesRecord(verb);
  const links = useMemo(() => (closing ? [...openLinks] : []), [closing, openLinks]);
  const reasonNeeded = REASON_REQUIRED.includes(verb);

  const build = useMemo<VerbBuilder>(() => {
    const takes = closureTakes(verb);
    const recordActor = { upn: actor.upn, ...(actor.lookupId ? { siteUserId: actor.lookupId } : {}) };
    return (base, input, now) => {
      const extra = input.extra ?? {};
      const closure: ClosureInput = {
        ...(takes.includes('set') && Object.keys(input.set).length > 0 ? { set: input.set } : {}),
        ...(takes.includes('reason') && input.reason ? { reason: input.reason } : {}),
        ...(takes.includes('closedOn') && (extra.closedOn ?? '').trim() ? { closedOn: extra.closedOn } : {}),
        ...(takes.includes('nextReviewOn') && (extra.nextReviewOn ?? '').trim() ? { nextReviewOn: extra.nextReviewOn } : {}),
        ...(takes.includes('reviewNote') ? { reviewNote: extra.reviewNote ?? '' } : {}),
        ...(takes.includes('communicationNote') && (extra.communicationNote ?? '').trim() ? { communicationNote: extra.communicationNote } : {}),
        auditId: input.auditId,
      };
      // 13.2-08: the open checks, only when known. Absent (follow-ups loading or failed) → refused `checks-unknown`.
      return buildClosureVerb(verb, base, recordActor, closure, now, {
        preparedByUpn: preparer,
        openLinks: links,
        ...(checksOpen !== undefined ? { openChecks: checksOpen } : {}),
      });
    };
  }, [verb, actor, preparer, links, checksOpen]);

  const word = ANYWAY_WORD[verb] ?? CLOSURE_LABELS[verb];
  const confirmNote = () =>
    links.length > 0 ? (
      <p className={styles.confirmNote}>
        <strong>
          {links.length === 1 ? '1 link is still open' : `${links.length} links are still open`}: {links.join(', ')}.
        </strong>{' '}
        {word} anyway? Closing doesn't wait for them.
      </p>
    ) : null;

  return (
    <div className={styles.verbBody}>
      <p className={styles.verbIntro}>{introOf(verb, register)}</p>
      {verb === 'readyForApproval' && register === 'incidents' && <DraftingHelp row={row} followUps={followUps} />}
      <VerbForm
        register={register}
        verb={verb}
        row={row}
        siblings={siblings}
        actor={actor}
        columns={columns}
        build={build}
        extraFields={extraFields}
        {...(reasonNeeded
          ? { reasonPrompt: { label: 'Why (goes on the trail)', hint: `At least ${REASON_MIN} characters. No phone numbers or email addresses.` } }
          : {})}
        confirmNote={confirmNote}
        quietWarnings={['open-link']}
        elsewhere={(column) => recordedUnder(register, column)}
        saveLabel={links.length > 0 ? `${BUTTON_WORDS[verb]} anyway` : BUTTON_WORDS[verb]}
        saveTone={SOLEMN.has(verb) ? 'solemn' : 'go'}
        onSaved={onSaved}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 13.2-10: close as a duplicate (D-13.2-P-10), its own step
// ---------------------------------------------------------------------------

const KEPT_KEY = 'keptRef';

function DuplicateStep({ row, siblings, actor, onSaved }: Omit<PanelProps, 'openLinks'>) {
  const { writer, io } = useActionRuntime();
  const duplicates = useContext(DuplicatesContext);
  const top = duplicates?.status === 'ready' ? (duplicates.matches[0] ?? null) : null;
  // The kept ref starts as the top possible match. A match that lands after the step opened fills it, until typed in.
  const [touched, setTouched] = useState(false);
  const [initialRef, setInitialRef] = useState(top?.ref ?? '');
  const [typed, setTyped] = useState(initialRef);
  if (!touched && top && initialRef !== top.ref) {
    setInitialRef(top.ref);
    setTyped(top.ref);
  }
  const kept = useKeptRecord(row, typed);
  const keptRow = kept.status === 'ready' ? kept.row : null;

  const build = useMemo<VerbBuilder>(() => {
    const recordActor = { upn: actor.upn, ...(actor.lookupId ? { siteUserId: actor.lookupId } : {}) };
    return (base, input, now) =>
      buildClosureVerb(
        'closeAsDuplicate',
        base,
        recordActor,
        { keptRef: input.extra?.[KEPT_KEY] ?? '', ...(input.reason ? { reason: input.reason } : {}), auditId: input.auditId },
        now,
        { kept: keptRow },
      );
  }, [actor, keptRow]);

  const extraFields = useMemo<VerbExtraField[]>(
    () => [
      {
        key: KEPT_KEY,
        column: CLOSURE_INPUT_COLUMN.keptRef as string,
        label: 'The incident you are keeping',
        kind: 'text',
        initial: initialRef,
        hint: top
          ? `As INC- and its number. Filled with the likeliest match, ${top.ref}; change it if another is the one to keep.`
          : 'As INC- and its number, for example INC-11.',
      },
    ],
    [initialRef, top],
  );

  // After the save landed: the kept record's Note goes on ITS trail. Owed (persisted, retried) if it doesn't post.
  const afterCommit = async (built: Extract<RecordVerbBuild, { ok: true }>): Promise<VerbAfterCommit | null> => {
    const rows = (built as Extract<DuplicateBuild, { ok: true }>).keptSiblings ?? [];
    const keptRef = rows[0]?.ParentRef ?? kept.ref;
    if (rows.length === 0 || !io || !keptRef) return null;
    try {
      const r = await writer.postRecordSiblings(io as RecordIO, keptRef, rows, actor);
      return { ref: keptRef, count: r.auditPending.length };
    } catch {
      return { ref: keptRef, count: rows.length };
    }
  };

  const ticket = String(row.fields.ExternalId ?? '').trim();
  const confirmNote = () =>
    kept.ref ? (
      <p className={styles.confirmNote}>
        <strong>A note goes on {kept.ref}&rsquo;s trail too</strong>, naming this incident
        {ticket ? ` and its ShiftCare ticket ${ticket}, which stays on this record so the daily reconcile still finds it` : ''}.
      </p>
    ) : null;

  return (
    <div className={styles.verbBody}>
      <p className={styles.verbIntro}>{introOf('closeAsDuplicate', row.register)}</p>
      <VerbForm
        key={`dup|${initialRef}`}
        register={row.register}
        verb="closeAsDuplicate"
        row={row}
        siblings={siblings}
        actor={actor}
        columns={[]}
        build={build}
        extraFields={extraFields}
        onExtraChange={(key, value) => {
          if (key !== KEPT_KEY) return;
          setTouched(true);
          setTyped(value);
        }}
        lead={<KeptLead duplicates={duplicates} kept={kept} />}
        reasonPrompt={{ label: 'A note (optional)', hint: 'It goes on both trails after “Duplicate of INC-n.”. No phone numbers or email addresses.' }}
        confirmNote={confirmNote}
        saveBlocked={kept.status === 'loading'}
        resetKey={`${kept.status}|${kept.ref ?? ''}`}
        afterCommit={afterCommit}
        saveLabel={BUTTON_WORDS.closeAsDuplicate}
        saveTone="solemn"
        onSaved={onSaved}
      />
    </div>
  );
}

/** Above the kept-ref input: the possible matches, in words, and the record the ref names (or why it can't be read). */
function KeptLead({ duplicates, kept }: { duplicates: ContextType<typeof DuplicatesContext>; kept: KeptRecord }) {
  const matches = duplicates?.status === 'ready' ? duplicates.matches : [];
  const title = kept.row && typeof kept.row.fields.Title === 'string' ? kept.row.fields.Title.trim() : '';
  return (
    <div className={styles.field}>
      {matches.length > 0 && (
        <p className={styles.hint} data-testid="duplicate-matches">
          Possible {matches.length === 1 ? 'match' : 'matches'}:{' '}
          {matches.map((m, i) => (
            <span key={m.ref}>
              {i > 0 && '; '}
              <Link to={`/record/${m.ref}`}>{m.ref}</Link>
              {duplicates?.titles[m.ref] ? ` (${duplicates.titles[m.ref]})` : ''}: {matchWords(m)}
            </span>
          ))}
          . A person decides: nothing is closed on a match alone.
        </p>
      )}
      <p className={styles.hint} role="status" aria-live="polite" data-testid="kept-record">
        {kept.status === 'loading' && `Reading ${kept.ref}…`}
        {kept.status === 'ready' && kept.row && (
          <>
            Keeping <strong>{kept.ref}</strong>
            {title ? `: ${title}` : ''} ({String(kept.row.fields.Status ?? 'no status')}).
          </>
        )}
        {kept.status === 'not-found' && `${kept.ref} isn't on the register. Check the number.`}
        {kept.status === 'error' && `${kept.ref} couldn't be read${kept.error ? `: ${kept.error}` : '.'}`}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 13.2-08: the drafting help (D-13.2-A-2, D-13.2-P-9)
// ---------------------------------------------------------------------------

/** A tool's name, linked only when its address is confirmed. */
function ToolName({ tool }: { tool: DraftingTool }) {
  return tool.url ? (
    <a href={tool.url} target="_blank" rel="noopener noreferrer">
      {tool.name}
    </a>
  ) : (
    <>{tool.name}</>
  );
}

/** The clipboard fallback: a hidden textarea and the copy command. False when the browser allows neither. */
function copyByCommand(text: string): boolean {
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  try {
    area.select();
    return typeof document.execCommand === 'function' && document.execCommand('copy');
  } catch {
    return false;
  } finally {
    area.remove();
  }
}

function DraftingHelp({ row, followUps }: { row: RecordRow; followUps: readonly FollowUp[] | null }) {
  const [copied, setCopied] = useState<'idle' | 'copied' | 'manual'>('idle');
  const brief = useMemo(() => closureBrief(row, followUps), [row, followUps]);
  const tools = draftingToolsFor(row.fields.BusinessUnit);

  async function copy() {
    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) throw new Error('no clipboard');
      await navigator.clipboard.writeText(brief);
      setCopied('copied');
    } catch {
      setCopied(copyByCommand(brief) ? 'copied' : 'manual');
    }
  }

  return (
    <div className={checkStyles.draft}>
      <p className={checkStyles.flat}>
        To draft these answers quickly, copy this incident&apos;s facts and the questions, then paste them into{' '}
        {tools.map((t, i) => (
          <span key={t.key}>
            {i > 0 && ' or '}
            <ToolName tool={t} /> ({t.purpose})
          </span>
        ))}
        . Check obligations in <ToolName tool={DRAFTING_TOOLS.advisor} />, then write your answers below.
      </p>
      <div className={checkStyles.draftRow}>
        <Button type="button" variant="secondary" size="sm" onClick={() => void copy()}>
          Copy for Nina / Hana
        </Button>
        {copied === 'copied' && (
          <span className={styles.hint} role="status">
            Copied
          </span>
        )}
      </div>
      {copied === 'manual' && (
        <div className={styles.field}>
          <p className={styles.hint} role="status">
            The browser didn&apos;t allow copying. Select the text below and copy it.
          </p>
          <textarea className={checkStyles.manual} readOnly value={brief} aria-label="The closure drafting brief" rows={8} />
        </div>
      )}
    </div>
  );
}
